import { formatDate } from '@angular/common';
import {
  PhoneReport,
  PhoneReportConnectivityInfo,
  PhoneReportConnectivityIssueDetails,
  PhoneReportConnectivityIssueType,
  PhoneReportConnectivityStatus,
  PhoneReportIssue,
} from '../models/phone-report.model';
import { PingWeightEnum } from '../models/ping.model';

export class PhoneReportConnectivityUtils {
  static computeConnectivityInfo(report: PhoneReport): PhoneReportConnectivityInfo {
    const issues: PhoneReportConnectivityIssueDetails[] = [];

    if (!report?.testCreatedAt || !report?.testTtl) {
      return {
        status: PhoneReportConnectivityStatus.UNAVAILABLE,
        description: 'Connectivity is unavailable: missing test window data.',
        issues: [],
      };
    }

    const EXPECTED_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes
    const TOLERANCE_MS = 30 * 1000; // ±30s
    const WINDOW_PADDING_MS = 5 * 60 * 1000; // 5 minutes

    const createdAtMs = report.testCreatedAt.getTime();
    const windowStart = createdAtMs - WINDOW_PADDING_MS;
    const windowEnd = createdAtMs + report.testTtl * 1000 + WINDOW_PADDING_MS;

    const roundedWindowStart = this.ceilToMinute(new Date(windowStart)).getTime();
    const roundedWindowEnd = this.floorToMinute(new Date(windowEnd)).getTime();

    if (this.isCollectingPings(report)) {
      const baseReadyMs = createdAtMs + report.testTtl * 1000 + WINDOW_PADDING_MS;
      const eta = this.roundToCronBoundary(new Date(baseReadyMs));

      return {
        status: PhoneReportConnectivityStatus.COLLECTING,
        description: `Collecting connectivity data. Will be calculated around: ${formatDate(eta, 'dd MMM HH:mm:ss', 'en')}`,
        issues: [],
      };
    }

    // Normalize + in-window filter
    const allPings = [...(report.pings ?? [])]
      .filter(p => p?.timestamp)
      .map(p => ({ ...p, _ts: +new Date(p.timestamp) }))
      .sort((a, b) => a._ts - b._ts);

    const windowPings = allPings.filter(p => p._ts >= windowStart && p._ts <= windowEnd);

    // No pings: emit a missing event for every expected slot across the window.
    if (windowPings.length === 0) {
      const startOnMinute = this.roundToMinute(new Date(roundedWindowStart)).getTime();

      for (let t = startOnMinute; t <= roundedWindowEnd; t += EXPECTED_INTERVAL_MS) {
        const timestampCurrent = this.roundToMinute(new Date(t));

        issues.push({
          type: PhoneReportConnectivityIssueType.MISSING_PING,
          details: `Expected ping around ${new Date(t).toString()}`,
          timestampCurrent,
          timestampPrev: null,
        });
      }
      return finalize(issues);
    }

    // Sequence inside window
    const seq = windowPings;

    // head-gap check (roundedWindowStart -> first ping)
    {
      const first = seq[0];
      const headGap = first._ts - roundedWindowStart;
      const tooLargeHead = headGap > EXPECTED_INTERVAL_MS + TOLERANCE_MS;
      if (tooLargeHead) {
        issues.push({
          type: PhoneReportConnectivityIssueType.IRREGULAR_INTERVAL,
          details: `Irregular interval ${this.formatGap(roundedWindowStart, first._ts)} (expected ~2m) from window start ${this.formatDate(windowStart)} to first ping ${this.formatDate(first._ts)}`,
          timestampCurrent: new Date(first._ts),
          timestampPrev: new Date(roundedWindowStart),
        });
      }
    }

    // degraded strength + irregular intervals
    for (let i = 0; i < seq.length; i++) {
      const curr = seq[i];
      const currTs = curr._ts;
      const currDt = new Date(currTs);
      const weight = curr.pingWeight;

      const goodSignals = [PingWeightEnum.MEDIUM, PingWeightEnum.HIGH];

      if (!goodSignals.includes(weight)) {
        issues.push({
          type: PhoneReportConnectivityIssueType.DEGRADED_SIGNAL,
          details: `${String(weight)}`,
          timestampCurrent: currDt,
          timestampPrev: null,
        });
      }

      if (i === 0) continue;

      const prev = seq[i - 1];
      const prevTs = prev._ts;
      const gap = currTs - prevTs;

      const tooLarge = gap > 0 && gap > EXPECTED_INTERVAL_MS + TOLERANCE_MS;
      const tooSmall = gap > 0 && gap < EXPECTED_INTERVAL_MS - TOLERANCE_MS;

      if (tooLarge || tooSmall) {
        issues.push({
          type: PhoneReportConnectivityIssueType.IRREGULAR_INTERVAL,
          details: `Irregular interval ${this.formatGap(prevTs, currTs)} (expected ~2m) from ${this.formatDate(prevTs)} to ${this.formatDate(currTs)}`,
          timestampCurrent: currDt, // emit at the *current* ping
          timestampPrev: new Date(prevTs), // range context
        });
      }
    }

    // tail-gap check (last ping -> roundedWindowEnd)
    {
      const last = seq[seq.length - 1];
      const tailGap = roundedWindowEnd - last._ts;
      const tooLargeTail = tailGap > EXPECTED_INTERVAL_MS + TOLERANCE_MS;
      if (tooLargeTail) {
        issues.push({
          type: PhoneReportConnectivityIssueType.IRREGULAR_INTERVAL,
          details: `Irregular interval ${this.formatGap(last._ts, roundedWindowEnd)} (expected ~2m) from last ping ${this.formatDate(last._ts)} to window end ${this.formatDate(windowEnd)}`,
          timestampCurrent: new Date(roundedWindowEnd),
          timestampPrev: new Date(last._ts),
        });
      }
    }

    return finalize(issues);

    function finalize(allIssues: PhoneReportConnectivityIssueDetails[]) {
      const status = allIssues.length === 0 ? PhoneReportConnectivityStatus.OK : PhoneReportConnectivityStatus.WARNING;
      return {
        status,
        description: 'Connectivity issues detected. Click to view details',
        issues: allIssues.sort((a, b) => +a.timestampCurrent - +b.timestampCurrent),
      };
    }
  }

  static shouldShowConnectivityIssueModal(issue: PhoneReportIssue) {
    return issue === PhoneReportIssue.DOES_NOT_SHOW || issue === PhoneReportIssue.EXPIRED_SIM;
  }

  static formatGap(fromMs: number, toMs: number) {
    const diff = Math.max(0, toMs - fromMs);
    const min = Math.floor(diff / 60000);
    return `${min}m`;
  }

  static formatDate(date: Date | number) {
    return formatDate(date, 'dd MMM HH:mm:ss', 'en');
  }

  static isCollectingPings(report: PhoneReport): boolean {
    if (report.pingsCollected) return false;

    const now = Date.now();
    const testCreatedAtTime = report.testCreatedAt.getTime();

    // pings can be collected only for tests that are less than 2 days old
    const twoDaysMs = 2 * 24 * 60 * 60 * 1000;
    if (now - testCreatedAtTime > twoDaysMs) {
      return false;
    }

    return true;
  }

  static roundToCronBoundary(d: Date): Date {
    const res = new Date(d.getTime());
    const m = res.getMinutes();
    const s = res.getSeconds();
    const ms = res.getMilliseconds();

    // "More than 30 minutes" → next hour HH:00
    // Exactly 30:00.000 → keep HH:30; 30:00.001+ → next hour.
    if (m > 30 || (m === 30 && (s > 0 || ms > 0))) {
      res.setHours(res.getHours() + 1, 0, 0, 0);
    } else {
      res.setMinutes(30, 0, 0);
    }
    return res;
  }

  static roundToMinute(d: Date): Date {
    const r = new Date(d.getTime());
    r.setSeconds(0, 0);
    return r;
  }

  static floorToMinute(d: Date): Date {
    const r = new Date(d.getTime());
    r.setSeconds(0, 0);
    return r;
  }

  static ceilToMinute(d: Date): Date {
    const r = new Date(d.getTime());
    const hasSubMinute = r.getSeconds() !== 0 || r.getMilliseconds() !== 0;
    r.setSeconds(0, 0);
    if (hasSubMinute) r.setMinutes(r.getMinutes() + 1);
    return r;
  }
}
